import { FolderLock } from '@phosphor-icons/react';
import { AnimatePresence } from 'framer-motion';
import React from 'react';
import { Flows, Folder } from '../../../../util/Types/Folder';
import { Space } from '../../../../util/Types/Sidebar';
import { AnimatedListItem } from '../dnd/AnimatedListItem';
import { CollapsibleGroup } from '../shared/CollapsibleGroup';
import { getFluxbotVirtualFolderId } from '../utils';
import { AgentItem } from './AgentItem';
import { FolderItem } from './FolderItem';
import { SidebarAddButton } from './SidebarAddButton';
import type { useAgentsSidebarHandlers } from './hooks';

type FlowHandlers = ReturnType<typeof useAgentsSidebarHandlers>['handlers'];
type FolderItemProps = Omit<
  React.ComponentProps<typeof FolderItem>,
  'folder' | 'spaceId' | 'ancestorReservedType'
>;

interface AgentsSectionBodyProps {
  spaceId: string | null;
  spaces: Space[];
  rootFluxbots: Flows[];
  rootAgents: Flows[];
  rootForms: Flows[];
  enrichedSharedFolder?: Folder | null;
  enrichedSubfolders: Folder[];
  folderItemProps: FolderItemProps;
  handlers: FlowHandlers;
  onCreateFluxbot: () => void;
  onCreateAgent: () => void;
  onCreateInterface: () => void;
}

export const AgentsSectionBody: React.FC<AgentsSectionBodyProps> = ({
  spaceId,
  spaces,
  rootFluxbots,
  rootAgents,
  rootForms,
  enrichedSharedFolder,
  enrichedSubfolders,
  folderItemProps,
  handlers,
  onCreateFluxbot,
  onCreateAgent,
  onCreateInterface,
}) => {
  const agentsFolderId = spaceId ? `${spaceId}-Agents` : 'private-Agents';

  const renderFlowItem = (
    flow: Flows,
    options: {
      ancestorReservedType: 'agents' | 'fluxbots';
      forceFluxbotIcon?: boolean;
      folderId: string;
      keyPrefix: string;
    },
  ) => (
    <AnimatedListItem key={`${options.keyPrefix}:${flow.id}`} itemKey={flow.id}>
      <AgentItem
        flow={flow}
        folderId={options.folderId}
        forceFluxbotIcon={options.forceFluxbotIcon}
        isNested
        spaceId={spaceId}
        ancestorReservedType={options.ancestorReservedType}
        onRename={handlers.handleFlowRename}
        onDelete={handlers.handleFlowDelete}
        onShare={handlers.handleFlowShare}
        onOpenInNewTab={handlers.handleFlowOpenInNewTab}
        onCreateInterface={handlers.handleCreateInterface}
        onAddToLibrary={handlers.handleAddToLibrary}
        onDuplicate={handlers.handleFlowDuplicate}
      />
    </AnimatedListItem>
  );

  return (
    <>
      {rootFluxbots.length > 0 && (
        <CollapsibleGroup
          icon={FolderLock}
          title='Fluxbots'
          action={<SidebarAddButton onClick={onCreateFluxbot} />}
        >
          <AnimatePresence initial={false}>
            {rootFluxbots.map(flow =>
              renderFlowItem(flow, {
                ancestorReservedType: 'fluxbots',
                forceFluxbotIcon: true,
                folderId: getFluxbotVirtualFolderId(flow, spaces),
                keyPrefix: 'fluxbot',
              }),
            )}
          </AnimatePresence>
        </CollapsibleGroup>
      )}
      {rootAgents.length > 0 && (
        <CollapsibleGroup
          icon={FolderLock}
          title='Agents'
          action={<SidebarAddButton onClick={onCreateAgent} />}
        >
          <AnimatePresence initial={false}>
            {rootAgents.map(agent =>
              renderFlowItem(agent, {
                ancestorReservedType: 'agents',
                folderId: agentsFolderId,
                keyPrefix: 'agent',
              }),
            )}
          </AnimatePresence>
        </CollapsibleGroup>
      )}
      {rootForms.length > 0 && (
        <CollapsibleGroup
          icon={FolderLock}
          title='Interfaces'
          action={<SidebarAddButton onClick={onCreateInterface} />}
        >
          <AnimatePresence initial={false}>
            {rootForms.map(form =>
              renderFlowItem(form, {
                ancestorReservedType: 'agents',
                folderId: agentsFolderId,
                keyPrefix: 'form',
              }),
            )}
          </AnimatePresence>
        </CollapsibleGroup>
      )}
      {enrichedSharedFolder && (
        <FolderItem
          key={enrichedSharedFolder.id}
          folder={enrichedSharedFolder}
          spaceId={spaceId}
          ancestorReservedType={null}
          {...folderItemProps}
          onFolderRename={undefined}
          onFolderDelete={undefined}
        />
      )}
      <AnimatePresence initial={false}>
        {enrichedSubfolders.map(subfolder => (
          <AnimatedListItem
            key={`folder:${subfolder.id}`}
            itemKey={subfolder.id}
          >
            <FolderItem
              folder={subfolder}
              spaceId={spaceId}
              ancestorReservedType={null}
              {...folderItemProps}
            />
          </AnimatedListItem>
        ))}
      </AnimatePresence>
    </>
  );
};
